<template>
  <RevStickyBar
    :id="STICKY_BAR_TOP_ID"
    class="hidden flex-col md:flex"
    position="top"
    :style="{ right: `${squeezeOffsetPx}px` }"
    :visible="showBar"
  >
    <RevContainer :key="`${showBar}`" ref="topBarRef" class="w-full py-1">
      <div class="flex items-center gap-12">
        <template v-if="!isChristmasTime">
          <RevIllustration
            v-if="productImage"
            :alt="productImage.description"
            class="mr-4"
            :height="51"
            sizes="50vw"
            :src="productImage.url"
            :width="51"
          />
          <div class="flex flex-wrap items-center">
            <template v-for="(item, index) in selectedTitle" :key="item.value">
              <div
                v-if="upgradePickerFields && item.upgraded"
                class="relative shrink-0"
                :class="[$style.shimmerText]"
              >
                <span
                  :key="item.value"
                  class="rounded-md inline-block p-4 text-transparent"
                  :class="{
                    [$style.animateShimmer]:
                      upgradePickerFields && item.upgraded,
                  }"
                >
                  {{ item.value }}
                </span>
              </div>

              <div
                v-if="!(upgradePickerFields && item.upgraded)"
                class="rounded-md inline-block shrink-0 p-4 max-w-full"
              >
                {{ item.value }}
              </div>
              <div v-if="index !== selectedTitle.length - 1" class="mx-4">
                -
              </div>
            </template>
          </div>
        </template>

        <template v-else>
          <div class="shrink-0">
            <RevIllustration
              v-if="hasDeliveryBeforeChristmas"
              alt=""
              :height="36"
              src="/img/product/delivery-before-christmas.svg"
              :width="36"
            />
            <IconTruckExpress v-else />
          </div>
          <ShippingBlock :shipping="selectedOffer.shipping" />
        </template>

        <div class="flex grow flex-col items-end">
          <slot name="price">
            <Price
              :flash-sales-discount
              :mobile-plan="selectedMobilePlan"
              :price
              :price-due-today="priceWithoutSubsidies"
              :product
              :selected-offer
              variant="compact"
              :with-assurance-fee="withQualityAssuranceFeeTop"
            />
          </slot>
        </div>
        <div class="ml-20 w-full max-w-256">
          <RevButton
            :class="buttonClass"
            data-qa="product-page-buy-button-sticky-bar"
            full-width="always"
            variant="primary"
            @click="onAddToCart"
          >
            {{ i18n(translations.addToCart) }}
          </RevButton>
        </div>
      </div>
    </RevContainer>

    <slot name="below-bar" />

    <slot name="progress-bar" />
  </RevStickyBar>
</template>

<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue'

import type { Price as PriceType } from '@backmarket/http-api/src/api-models/Price'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { RevIllustration } from '@ds/components/Illustration'
import { RevStickyBar } from '@ds/components/StickyBar'
import { IconTruckExpress } from '@ds/icons/IconTruckExpress'

import { useAddToCart } from '~/scopes/product-core/components/containers/ContainerAddToCart/composables/useAddToCart/useAddToCart'
import { useForceHideHeader } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/composables/useForceHideHeader/useForceHideHeader'
import Price from '~/scopes/product-core/components/presentationals/Price/Price.vue'
import { useProductTracking } from '~/scopes/product-core/composables/useFetch/useFetchProductTracking/composables/useProductTracking/useProductTracking'
import { useQualityServiceFeeEnabled } from '~/scopes/product-core/composables/useQualityServiceFeeEnabled/useQualityServiceFeeEnabled'
import type { PartnerNames } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useTelcoPicker/types'
import ShippingBlock from '~/scopes/shipping/components/ShippingBlock/ShippingBlock.vue'
import { useDeliveryBeforeChristmas } from '~/scopes/shipping/composables/useDeliveryBeforeChristmas/useDeliveryBeforeChristmas'
import { useShoppingAssistantSqueezeOffset } from '~/scopes/shopping-assistant/composables/useShoppingAssistantSqueezeOffset'

import { STICKY_BAR_TOP_ID } from './StickyBarTop.constants'
import translations from './StickyBarTop.translations'
import { useShowStickyBarTop } from './composables/useShowStickyBarTop'

const props = defineProps<{
  buttonClass?: string
  selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
  price: PriceType
  product: GetProductResponse
  selectedMobilePlan?: MobilePlanOffer
  tracking: ReturnType<typeof useProductTracking>
  priceWithoutSubsidies?: PriceType | null
  selectedOptions: string[]
  upgradePickerFields?: NonNullable<
    GetPickersResponse['upgrade']
  >['pickerFields']
  telcoSelection?: {
    isPickerActive: boolean
    selectedPartnerName: PartnerNames | null
  }
}>()

const i18n = useI18n()
const { addToCart } = useAddToCart()
const { isQualityServiceFeeEnabledTop: withQualityAssuranceFeeTop } =
  useQualityServiceFeeEnabled()

const { isChristmasTime, hasDeliveryBeforeChristmas } =
  useDeliveryBeforeChristmas(props.selectedOffer.shipping)

const selectedTitle = computed(() => {
  if (!props.upgradePickerFields) {
    return props.selectedOptions.map((option) => ({
      value: option,
      upgraded: false,
    }))
  }

  const upgradedFields = Object.values(props.upgradePickerFields)

  return props.selectedOptions.map((option, index) => {
    const upgradedField = upgradedFields[index]

    return {
      value: props.upgradePickerFields ? upgradedField.value : option,
      upgraded: upgradedField.upgrade,
    }
  })
})

const productImage = computed(() => {
  return props.product.images[0] ?? null
})

const flashSalesDiscount = computed(
  () =>
    props.product.promoCodes.find((promoCode) => promoCode.isFlashSale)
      ?.discountValue,
)

function onAddToCart() {
  addToCart({
    listingId: props.selectedOffer.offerLegacyId,
    listingPublicId: props.selectedOffer?.offerId,
    tracking: {
      ...props.tracking.product.value,
      list: 'header',
      campaign_id: props.selectedOffer.campaignId,
      population_id: props.selectedOffer.populationId,
    },
    partnerPromoCodes: props.product.includedServiceOffers.partnerPromoCodes,
    isTradeInEligible: props.product.isTradeInEligible,
    promoCodes: props.product.promoCodes,
    telcoSelection: props.telcoSelection,
  })
}

const { showBar } = useShowStickyBarTop()
const topBarRef = useTemplateRef('topBarRef')
useForceHideHeader(topBarRef)

// RevStickyBar is `fixed inset-x-0`, so it ignores the page's marginRight
// squeeze entirely — inset it from the right by the same amount so it
// doesn't render underneath the assistant panel.
const { squeezeOffsetPx } = useShoppingAssistantSqueezeOffset()
</script>

<style scoped module>
.shimmerText {
  background-image: linear-gradient(to right, #4055bf 50%, #2f3137 50%);
  background-size: 200% 100%;
  background-position: 100% 0;
  background-clip: text;
  animation: color-change-animation 2s forwards;
}

@keyframes color-change-animation {
  0% {
    background-position: 100% 0;
    @apply text-static-default-hi;
  }

  100% {
    background-position: 0 0;
    @apply text-static-brand-hi;
  }
}
</style>
